/**
 * LandingWhatsAppCTA — floating WhatsApp button shown on the public landing.
 *
 * Tightly coupled to ``/api/public/landing/whatsapp``:
 *   1) On mount we fetch the admin-configured business number plus a
 *      prefilled message scoped to the current language/country.
 *   2) If no number is configured we render nothing (admin hasn't
 *      finished onboarding the WhatsApp side).
 *   3) Clicking opens ``wa.me`` in a new tab — the conversation lands
 *      in the Admin → Comunicaciones → Chat inbox where a human (or
 *      LumiBot in auto-pause mode) can take it from there.
 *
 * Visual: a brand-green pill with the official WhatsApp glyph, anchored
 * to the bottom-right on desktop and the bottom-center on mobile so
 * it doesn't compete with the bottom nav of the in-app surface.
 * Auto-collapses to an icon-only chip after the first 12 seconds of
 * landing visit so it's not visual noise.
 */
import { useEffect, useState } from "react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export function LandingWhatsAppCTA({ lang = "es", country = null, planId = null, intent = "contact" }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(false); // animate in after small delay

  useEffect(() => {
    const params = new URLSearchParams({ intent, lang });
    if (country) params.set("country", country);
    if (planId) params.set("plan_id", planId);
    let cancel = false;
    fetch(`${API_BASE}/api/public/landing/whatsapp?${params}`, { credentials: "omit" })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (!cancel && j?.available) setData(j); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [lang, country, planId, intent]);

  // Slide in after 800ms. Only auto-collapse to icon-only on devices
  // with HOVER capability (desktop) — on touch devices the collapsed
  // 48px circle is harder to tap accurately AND collides visually
  // with the lead chat bubble. Keeping the full pill on mobile is
  // both more discoverable and more tappable.
  useEffect(() => {
    if (!data) return;
    const enter = setTimeout(() => setVisible(true), 800);
    const canHover = typeof window !== "undefined"
      && window.matchMedia && window.matchMedia("(hover: hover)").matches;
    const shrink = canHover ? setTimeout(() => setCollapsed(true), 12_000) : null;
    return () => { clearTimeout(enter); if (shrink) clearTimeout(shrink); };
  }, [data]);

  if (!data) return null;

  const label = lang === "en" ? "Chat on WhatsApp" : lang === "pt" ? "Falar no WhatsApp" : "Hablanos por WhatsApp";

  return (
    <a
      href={data.wa_link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => setCollapsed(false)}
      data-testid="landing-whatsapp-cta"
      data-wa-intent={intent}
      data-wa-plan={planId || ""}
      aria-label={label}
      className={`fixed z-40 bottom-5 right-4 lg:right-6 transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
    >
      <div className="group relative">
        {/* Glow halo on hover */}
        <span className="absolute inset-0 rounded-full bg-emerald-500/40 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        <div
          className={`relative inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FBE5B] text-white shadow-[0_18px_40px_-12px_rgba(37,211,102,0.6)] transition-all duration-300 ${
            collapsed ? "h-12 w-12 justify-center" : "h-12 pl-3 pr-4"
          }`}
          onMouseEnter={() => setCollapsed(false)}
        >
          {/* Official WhatsApp glyph (inline SVG, no icon-font dep) */}
          <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 fill-white" aria-hidden="true">
            <path d="M19.077 4.928A9.97 9.97 0 0 0 12 2c-5.514 0-10 4.486-10 10 0 1.762.464 3.476 1.345 4.984L2.05 22l5.158-1.348A9.96 9.96 0 0 0 12 22c5.514 0 10-4.486 10-10 0-2.67-1.039-5.181-2.923-7.072zM12 20.272a8.27 8.27 0 0 1-4.218-1.155l-.302-.18-3.063.8.82-2.987-.196-.31A8.26 8.26 0 0 1 3.728 12c0-4.563 3.71-8.272 8.272-8.272 2.21 0 4.288.861 5.852 2.426A8.218 8.218 0 0 1 20.272 12c0 4.563-3.71 8.272-8.272 8.272z" />
            <path d="M16.564 14.222c-.247-.124-1.46-.72-1.687-.802-.226-.083-.39-.124-.555.124-.165.247-.636.802-.78.967-.143.165-.287.186-.534.062-.247-.124-1.045-.385-1.991-1.228-.736-.656-1.232-1.466-1.377-1.713-.144-.247-.015-.38.108-.504.111-.11.247-.288.371-.432.124-.144.165-.247.247-.412.083-.165.041-.31-.021-.432-.062-.124-.555-1.337-.761-1.83-.2-.481-.405-.416-.555-.424l-.473-.008c-.165 0-.432.062-.658.31-.226.247-.864.846-.864 2.064s.885 2.394 1.008 2.56c.124.165 1.741 2.66 4.22 3.732.589.254 1.05.406 1.408.52.591.188 1.13.161 1.555.098.474-.07 1.46-.596 1.667-1.171.206-.575.206-1.067.144-1.171-.062-.103-.226-.165-.474-.288z" />
          </svg>
          <span
            className={`whitespace-nowrap text-sm font-semibold transition-all duration-300 overflow-hidden ${
              collapsed ? "max-w-0 opacity-0" : "max-w-[220px] opacity-100"
            }`}
          >
            {label}
          </span>
        </div>
        {/* Notification dot — only visible when collapsed */}
        {collapsed && (
          <span className="absolute top-0 right-0 h-3 w-3 rounded-full bg-rose-500 ring-2 ring-white animate-pulse" />
        )}
      </div>
    </a>
  );
}
